import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { SERVICE_BY_SLUG, SERVICES } from "../data/services";
import { trackItemSelect } from "../utils/analytics";
import "../components/styles/ServicePage.css";

// 검색어 하나에 페이지 하나.
// 본문은 data/services.js 에 있고 여기는 틀만 잡는다. 문구를 고치려면
// 이 파일이 아니라 데이터 쪽을 보면 된다.
//
// 아래쪽에 실제 수행 사례를 붙이는 게 이 페이지의 핵심이다. 기업 담당자는
// "남들은 어떻게 했나" 를 찾아서 들어오고, 그 근거로 내부 보고를 쓴다.

const ServicePage = () => {
    const { slug } = useParams();
    const service = SERVICE_BY_SLUG[slug];
    const [cases, setCases] = useState([]);

    useEffect(() => {
        if (!service) return;
        let alive = true;
        fetch("/api/portfolio")
            .then((r) => (r.ok ? r.json() : []))
            .then((all) => {
                if (!alive) return;
                // 데이터에 적어 둔 순서 그대로 보여준다. 응답 순서를 따르면
                // 가장 잘 맞는 사례가 뒤로 밀린다.
                const picked = service.cases
                    .map((s) => all.find((p) => p.slug === s))
                    .filter(Boolean);
                setCases(picked);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [service]);

    if (!service) return <Navigate to="/portfolio" replace />;

    const others = SERVICES.filter((s) => s.slug !== service.slug);

    return (
        <div className="sch-svc">
            <div className="sch-wrap sch-svc__inner">

                <nav className="sch-svc__crumb" aria-label="현재 위치">
                    <Link to="/">홈</Link>
                    <span aria-hidden="true">—</span>
                    <span>{service.nav}</span>
                </nav>

                <header className="sch-svc__head">
                    <h1 className="sc-h2">{service.h1}</h1>
                    <p className="sch-svc__lede">{service.lede}</p>

                    <div className="sch-svc__price">
                        <dl>
                            <div>
                                <dt>비용 범위</dt>
                                <dd>{service.price.range}</dd>
                            </div>
                            <div>
                                <dt>기간</dt>
                                <dd>{service.price.period}</dd>
                            </div>
                        </dl>
                        <p className="sch-svc__priceNote">{service.priceNote}</p>
                        <p className="sch-svc__priceNote">
                            시장 시세 기준이며 기능 범위에 따라 달라집니다.{" "}
                            <Link to="/pricing">유형별 비용 자세히 보기</Link>
                        </p>
                    </div>

                    <Link
                        to="/custom-product-second"
                        className="sc-btn sch-svc__cta"
                        onClick={() => trackItemSelect(`service:${service.slug}`)}
                    >
                        무료 상담 신청
                    </Link>
                </header>

                {service.sections.map((sec) => (
                    <section className="sch-svc__sec" key={sec.h}>
                        <h2 className="sc-h3">{sec.h}</h2>
                        {sec.body.map((p, i) => <p key={i}>{p}</p>)}
                    </section>
                ))}

                <section className="sch-svc__sec">
                    <h2 className="sc-h3">무엇을 받으시나</h2>
                    <ul className="sch-svc__list">
                        {service.deliverables.map((d) => <li key={d}>{d}</li>)}
                    </ul>
                </section>

                {cases.length > 0 && (
                    <section className="sch-svc__sec">
                        <h2 className="sc-h3">수행 사례</h2>
                        <div className="sch-svc__cases">
                            {cases.map((c) => (
                                <Link
                                    to={`/portfolio/${c.slug}`}
                                    className="sch-svc__case"
                                    key={c.slug}
                                    onClick={() => trackItemSelect(`case:${c.slug}`)}
                                >
                                    <span className="sch-svc__caseCat">{c.category}</span>
                                    <strong>{c.title}</strong>
                                    <span className="sch-svc__caseSum">{c.summary}</span>
                                </Link>
                            ))}
                        </div>
                        <p className="sch-svc__more">
                            <Link to="/portfolio">수행 사례 전체 보기 →</Link>
                        </p>
                    </section>
                )}

                <section className="sch-svc__sec">
                    <h2 className="sc-h3">자주 묻는 것</h2>
                    <dl className="sch-svc__faq">
                        {service.faq.map((f) => (
                            <div key={f.q}>
                                <dt>{f.q}</dt>
                                <dd>{f.a}</dd>
                            </div>
                        ))}
                    </dl>
                </section>

                {service.related?.length > 0 && (
                    <section className="sch-svc__sec">
                        <h2 className="sc-h3">더 읽어 보기</h2>
                        <ul className="sch-svc__list">
                            {service.related.map((r) => (
                                <li key={r.to}><Link to={r.to}>{r.label}</Link></li>
                            ))}
                        </ul>
                    </section>
                )}

                <section className="sch-svc__end">
                    <h2 className="sc-h3">되는지 안 되는지만 먼저 알려드립니다</h2>
                    <p>
                        어떤 일을 반복하고 계신지 적어주시면 영업일 1~2일 안에 연락드립니다.
                        쓰시던 파일이나 화면 녹화를 같이 보내주시면 견적이 훨씬 빨라집니다.
                        상담과 견적은 무료입니다.
                    </p>
                    <Link
                        to="/custom-product-second"
                        className="sc-btn"
                        onClick={() => trackItemSelect(`service-end:${service.slug}`)}
                    >
                        상담 신청하기
                    </Link>
                </section>

                <nav className="sch-svc__others" aria-label="다른 서비스">
                    <h2 className="sc-h4">다른 분야</h2>
                    <ul>
                        {others.map((s) => (
                            <li key={s.slug}>
                                <Link to={`/service/${s.slug}`}>{s.nav}</Link>
                            </li>
                        ))}
                    </ul>
                </nav>

            </div>
        </div>
    );
};

export default ServicePage;
